import React from 'react';
import { EditableColumnType } from 'components/CustomTable/CustomTable';
import AdditionalInformation from 'components/AdditionalInformation/AdditionalInformation';
import { IBranch, DataType } from 'store/ducks/DataEntry/Branch/types';
import i18nIsoCountries from 'i18n-iso-countries';
import CustomSelect from 'components/CustomSelect';
import { DatePicker, Button, Input } from 'antd';
import dayjs from 'dayjs';
import * as Styled from '../Tables/DataEntry/DataEntry.styled';

export const getDefaultColumns = (
  handleDateChange: (key: number, field: string, value: string) => void,
  handleSave: (record: IBranch) => void,
  handleAddRow: (record: { key: React.Key }, index: number) => void,
  handleRemoveRow: (key: number) => void,
  handleInactivate: (record: IBranch) => void,
  countries: { value: string; label: string }[],
  t: any,
): EditableColumnType[] => [
  {
    title: (
      <Styled.TitleComponent>
        <div>
          <p>{t('BRANCH.NAME_OF_BRANCH')}</p>
          <p>B_01.03.0030</p>
        </div>
        <Styled.Asterisx>*</Styled.Asterisx>
        <AdditionalInformation
          color="red"
          description={t('BRANCH.NAME_OF_BRANCH_INFO')}
        />
      </Styled.TitleComponent>
    ),
    dataIndex: 'branch_name',
    editable: true,
    fixed: 'left',
    className: 'highlighted-column',
  },
  {
    title: (
      <Styled.TitleComponent>
        <div>
          <p>{t('BRANCH.LEI_OF_FINANCIAL_ENTITY')}</p>
          <p>B_01.03.0020</p>
        </div>
        <Styled.Asterisx>*</Styled.Asterisx>
        <AdditionalInformation
          color="red"
          description={t('BRANCH.LEI_OF_FINANCIAL_ENTITY_INFO')}
        />
      </Styled.TitleComponent>
    ),
    dataIndex: 'head_office_lei',
    editable: true,
    className: 'highlighted-column',
  },
  {
    title: (
      <Styled.TitleComponent>
        <div>
          <p>{t('BRANCH.IDENTIFICATION_CODE')}</p>
          <p>B_04.01.0040</p>
        </div>
        <Styled.Asterisx>*</Styled.Asterisx>
        <AdditionalInformation
          color="red"
          description={t('BRANCH.IDENTIFICATION_CODE_INFO')}
        />
      </Styled.TitleComponent>
    ),
    dataIndex: 'branch_identification_code',
    editable: true,
    className: 'highlighted-column',
  },
  {
    title: (
      <>
        {t('BRANCH.BRANCH_FROM_TO')}
        <Styled.Asterisx>*</Styled.Asterisx>
      </>
    ),
    dataIndex: 'branch_validity',
    editable: false,
    render: (_: any, record: DataType) => {
      const startDate = record.start_date;
      const endDate = record.end_date;

      return (
        <Styled.DateInputs>
          <DatePicker
            value={startDate ? dayjs(startDate) : dayjs('1900-01-01')}
            format="YYYY-MM-DD"
            onClick={(e) => e.stopPropagation()}
            onChange={(date) => {
              const formattedDateString = date
                ? dayjs(date).format('YYYY-MM-DD')
                : '1900-01-01';

              handleDateChange(record.key, 'start_date', formattedDateString);
            }}
          />
          <DatePicker
            value={endDate ? dayjs(endDate) : dayjs('9999-01-01')}
            format="YYYY-MM-DD"
            onClick={(e) => e.stopPropagation()}
            onChange={(date) => {
              const formattedDateString = date
                ? dayjs(date).format('YYYY-MM-DD')
                : '9999-01-01';

              handleDateChange(record.key, 'end_date', formattedDateString);
            }}
          />
        </Styled.DateInputs>
      );
    },
  },
  {
    title: t('BRANCH.BRANCH_DESCRIPTION'),
    dataIndex: 'description_branch',
    editable: true,
    render: (text: string) => <Input value={text} />,
  },
  {
    title: (
      <Styled.TitleComponent>
        <div>
          <p>{t('BRANCH.COUNTRY_OF_BRANCH')}</p>
          <p>B_01.03.0040</p>
        </div>
        <Styled.Asterisx>*</Styled.Asterisx>
        <AdditionalInformation
          color="red"
          description={t('BRANCH.COUNTRY_OF_BRANCH_INFO')}
        />
      </Styled.TitleComponent>
    ),
    dataIndex: 'id_country',
    editable: false,
    className: 'highlighted-column',
    render: (_, record) => (
      <CustomSelect
        options={countries}
        value={record.id_country}
        onChange={(value: any) => {
          const selectedCountry = countries.find(
            (country) => country.value === value,
          );

          if (selectedCountry) {
            const countryName = selectedCountry.label;

            const isoCode = i18nIsoCountries.getAlpha2Code(countryName, 'en');

            handleSave({
              ...record,
              id_country: value,
              country_name: selectedCountry?.label || '',
              iso_code: isoCode || '',
            } as IBranch);
          }
        }}
        filterOption={(input: string, option: { label: string }) =>
          option?.label?.toLowerCase().includes(input.toLowerCase())
        }
      />
    ),
  },
  {
    title: <>{t('BRANCH.SELECTED_COUNTRY_ISO_CODE')}</>,
    dataIndex: 'iso_code',
    editable: true,
    className: 'highlighted-column',
    render: (text: string) => <Input value={text} disabled />,
  },
  {
    title: t('GENERAL.ACTIONS'),
    key: 'actions',
    render: (_: any, record) => (
      <>
        {record.isNew ? (
          <Button danger onClick={() => handleRemoveRow(record.key)}>
            {t('GENERAL.REMOVE')}
          </Button>
        ) : (
          <Button danger onClick={() => handleInactivate(record as IBranch)}>
            {t('GENERAL.SOFT_DELETE')}
          </Button>
        )}
      </>
    ),
  },
];
